// services/softProgram.service.ts

import {
  AsyncSelectFetchParams,
  AsyncSelectFetchResult,
} from "@/components/form/types";

export type SoftProgramStatus = "completed" | "ongoing" | "upcoming";
export type SoftProgramMode = "On-site" | "Online" | "Hybrid";

export interface SoftProgramType {
  _id: string;
  name: string; // e.g. "Leadership Development"
  vendor: string; // Vendor ID
  skillsCovered: string[]; // e.g. ["Communication", "Leadership"]
  students: string[]; // Array of Student IDs
  departments: string[]; // Array of Dept IDs
  batches: string[]; // Array of Batch IDs
  duration: string; // e.g. "2 weeks"
  maxParticipants: number;
  mode: SoftProgramMode;
  startDate: string; // ISO date string
  endDate: string; // ISO date string
  status: SoftProgramStatus;
  programObjectives: string;
  programDescription: string;
  createdAt: string;
  updatedAt: string;
}

export interface SoftProgramCreateType {
  name: string;
  vendor: string;
  skillsCovered: string[];
  students: string[];
  departments: string[];
  batches: string[];
  duration: string;
  maxParticipants: number;
  mode: SoftProgramMode;
  startDate: string;
  endDate: string;
  status: SoftProgramStatus;
  programObjectives: string;
  programDescription: string;
}

export interface SoftProgramMetaType {
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type SoftProgramParamsType = {
  search?: string;
  status?: SoftProgramStatus | "all";
  page?: number;
  limit?: number;
};

// ─── Dummy Data ───────────────────────────────────────────────────────────────

const DUMMY_PROGRAMS: SoftProgramType[] = [
  {
    _id: "1",
    name: "Leadership Development",
    vendor: "1", // LeadersEdge Institute
    skillsCovered: ["Communication", "Leadership"],
    students: ["1", "2"],
    departments: ["1", "2"],
    batches: ["1", "2"],
    duration: "2 weeks",
    maxParticipants: 50,
    mode: "On-site",
    startDate: "2025-01-15",
    endDate: "2025-01-29",
    status: "ongoing",
    programObjectives: "Develop leadership skills",
    programDescription: "Intensive 2-week program",
    createdAt: "2025-01-01T00:00:00.000Z",
    updatedAt: "2025-01-01T00:00:00.000Z",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const simulateDelay = (ms = 400) =>
  new Promise((resolve) => setTimeout(resolve, ms));

const applyFilters = (
  programs: SoftProgramType[],
  filters: SoftProgramParamsType
): { data: SoftProgramType[]; total: number } => {
  let result = [...programs];

  if (filters.search) {
    const q = filters.search.toLowerCase();
    result = result.filter(
      (p) =>
        p.name.toLowerCase().includes(q) || p.vendor.toLowerCase().includes(q)
    );
  }

  if (filters.status && filters.status !== "all") {
    result = result.filter((p) => p.status === filters.status);
  }

  const total = result.length;
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 10;
  result = result.slice((page - 1) * limit, page * limit);

  return { data: result, total };
};

// ─── Service Functions ────────────────────────────────────────────────────────

export const fetchAllSoftPrograms = async (filters: SoftProgramParamsType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get("/soft-programs/", { params: filters });
  const limit = filters.limit ?? 10;
  const page = filters.page ?? 1;
  const { data, total } = applyFilters(DUMMY_PROGRAMS, filters);
  return {
    programs: data,
    metaData: {
      pagination: { total, page, limit, totalPages: Math.ceil(total / limit) },
    } as SoftProgramMetaType,
  };
};

export const fetchSoftProgramById = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.get(`/soft-programs/${_id}`);
  const program = DUMMY_PROGRAMS.find((p) => p._id === _id);
  if (!program) throw new Error("Program not found");
  return program;
};

export const createSoftProgram = async (data: SoftProgramCreateType) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.post("/soft-programs/", data);
  const newProgram: SoftProgramType = {
    ...data,
    _id: String(Date.now()),
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
  DUMMY_PROGRAMS.push(newProgram);
  return newProgram;
};

export const updateSoftProgram = async (
  _id: string,
  data: SoftProgramCreateType
) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.put(`/soft-programs/${_id}`, data);
  const index = DUMMY_PROGRAMS.findIndex((p) => p._id === _id);
  if (index === -1) throw new Error("Program not found");
  DUMMY_PROGRAMS[index] = {
    ...DUMMY_PROGRAMS[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
  return DUMMY_PROGRAMS[index];
};

export const deleteSoftProgram = async (_id: string) => {
  await simulateDelay();
  // TODO: const res = await axiosInstance.delete(`/soft-programs/${_id}`);
  const index = DUMMY_PROGRAMS.findIndex((p) => p._id === _id);
  if (index === -1) throw new Error("Program not found");
  const [deleted] = DUMMY_PROGRAMS.splice(index, 1);
  return deleted;
};

export const fetchSoftSkillVendorOptions = async (
  _params: import("@/components/form/types").AsyncSelectFetchParams
): Promise<import("@/components/form/types").AsyncSelectFetchResult> => {
  await simulateDelay();
  return {
    items: [
      { label: "LeadersEdge Institute", value: "1" },
      { label: "SkillUp Training", value: "2" },
    ],
    hasMore: false,
    total: 2,
  };
};

export const fetchSoftSkillOptions = async (
  _params: AsyncSelectFetchParams
): Promise<AsyncSelectFetchResult> => {
  await simulateDelay();
  return {
    items: [
      { label: "Communication", value: "Communication" },
      { label: "Leadership", value: "Leadership" },
    ],
    hasMore: false,
    total: 2,
  };
};

export const softProgramService = {
  fetchAllSoftPrograms,
  fetchSoftProgramById,
  createSoftProgram,
  updateSoftProgram,
  deleteSoftProgram,
  fetchSoftSkillVendorOptions,
  fetchSoftSkillOptions,
};
